import { defineComponent, createLibrary } from '@customerio/hydra-copilot/react';
import type { PromptOptions } from '@customerio/hydra-copilot/react';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardSection,
  Grid,
  InlineStack,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { open as openSidebar } from '@/features/sidebar';
import { isInternalLink, isSafeUrl, isSameWorkspaceLink } from '@/utils/url';
import { useMemo } from 'react';
import type { ComponentProps } from 'react';
import { z } from '@customerio/hydra-validation';
import { COPILOT_GRID_GAPS } from './openui-grid-gaps';
import { Chart, arc, area, bar, line, sankey, type ChartMark } from '@customerio/pluma-components/charts/react';
import { AppErrorBoundary } from '@/components/app-error-boundary';

// Register Series as a named type so the prompt shows `Series[]`
// instead of the verbose inline `{name: string, data: number[]}[]`.
const SeriesSchema = z.object({
  name: z.string(),
  data: z.array(z.number()),
});
// Zod 4 throws if the same id is registered twice. This can happen on HMR or
// test re-evaluation because each re-execution creates a new SeriesSchema
// instance, so `get(SeriesSchema)` returns null even if 'Series' is already
// claimed by the previous instance. A try/catch is the safest guard.
try {
  z.globalRegistry.add(SeriesSchema, { id: 'Series' });
} catch {
  // 'Series' id already registered from a previous module evaluation — safe to ignore.
}

const LinkButtonDef = defineComponent({
  name: 'LinkButton',
  description:
    'A button that navigates to a URL. Use at the end of a response to link the user to a relevant page (e.g. "View Transactional", "Go to Campaign").',
  props: z.object({
    label: z.string(),
    href: z.string(),
  }),
  component: ({ props }) => {
    const internal = isInternalLink(props.href);
    if (internal != null && isSameWorkspaceLink(props.href)) {
      return (
        <AppErrorBoundary>
          <InlineStack>
            <Button
              href={internal.relativePath}
              variant="primary"
              size="sm"
              icon="chevron-right"
              iconPosition="trailing"
              onClick={(event_) => {
                if (!event_.metaKey && !event_.ctrlKey) openSidebar('copilot');
              }}
            >
              {props.label}
            </Button>
          </InlineStack>
        </AppErrorBoundary>
      );
    }
    const safeHref = isSafeUrl(props.href) ? props.href : undefined;

    return (
      <AppErrorBoundary>
        <InlineStack>
          <Button href={safeHref} variant="secondary" size="sm" icon="exit" iconPosition="trailing" isExternal>
            {props.label}
          </Button>
        </InlineStack>
      </AppErrorBoundary>
    );
  },
});

// ─── Chart ───────────────────────────────────────────────────────────────────

const SankeyLinkSchema = z.object({
  from: z.string(),
  to: z.string(),
  weight: z.number(),
});

try {
  z.globalRegistry.add(SankeyLinkSchema, { id: 'SankeyLink' });
} catch {
  // Already registered — safe to ignore on HMR.
}

const CHART_TYPES = ['line', 'bar', 'column', 'pie', 'area', 'spline', 'sankey'] as const;
type GridGap = NonNullable<ComponentProps<typeof Grid>['gap']>;

type Series = z.infer<typeof SeriesSchema>;
type SankeyLink = z.infer<typeof SankeyLinkSchema>;

// While the fence is still streaming, the openui parser autocloses what it has
// so far and only validates top-level required props, so a series item or a
// link can reach us before all of its keys exist.
type ChartProps = {
  series?: Partial<Series>[];
  type?: (typeof CHART_TYPES)[number];
  categories?: string[];
  links?: Partial<SankeyLink>[];
};

// The pie default of the chart this replaced (innerSize: 55%).
const PIE_INNER_RADIUS = 0.55;

// Draw the complete items and skip the rest until the next chunk fills them in.
function completeSeries(series: Partial<Series>[] | undefined): Series[] {
  return (series ?? []).flatMap((s) => (Array.isArray(s.data) ? [{ name: s.name ?? '', data: s.data }] : []));
}

function completeLinks(links: Partial<SankeyLink>[]): SankeyLink[] {
  return links.flatMap((l) =>
    l.from != null && l.to != null && l.weight != null ? [{ from: l.from, to: l.to, weight: l.weight }] : [],
  );
}

type SankeyRendererProps = {
  links: Partial<SankeyLink>[];
};

function SankeyRenderer({ links }: SankeyRendererProps) {
  const marks = useMemo(
    () => [sankey({ data: completeLinks(links), source: 'from', target: 'to', value: 'weight', linkOpacity: 0.4 })],
    [links],
  );
  return <Chart ariaLabel="Flow diagram" height={400} marks={marks} />;
}

// The area fill of the chart this replaced: a full-opacity line per series
// with a light wash under it.
const AREA_FILL_OPACITY = 0.15;

type CartesianRow = {
  category: string | number;
  value: number;
};

type SeriesRow = CartesianRow & {
  series: string;
};

type PieSlice = {
  name: string;
  value: number;
};

function pieMarks(series: Series[], categories: string[] | undefined): ChartMark<PieSlice>[] {
  const [first] = series;
  if (first == null || categories == null) {
    return [];
  }
  const slices = categories.map((name, index) => ({ name, value: first.data[index] ?? 0 }));
  return [arc({ data: slices, value: 'value', series: 'name', innerRadius: PIE_INNER_RADIUS })];
}

// One row per (series, category) for the bar layouts: multiple series must
// share one mark, or every mark takes the category's full bandwidth and they
// pile on top of each other instead of standing side by side.
function toSeriesRows(series: Series[], categories: string[] | undefined): SeriesRow[] {
  return series.flatMap((s) =>
    s.data.map((value, index) => ({ category: categories?.[index] ?? index, series: s.name, value })),
  );
}

function cartesianMarks(
  type: 'line' | 'bar' | 'column' | 'area' | 'spline',
  series: Series[],
  categories: string[] | undefined,
): readonly ChartMark<CartesianRow>[] | readonly ChartMark<SeriesRow>[] {
  // Side-by-side series, like the grouped columns this replaced (a bar mark
  // stacks its series by default).
  if (type === 'column') {
    return [
      bar({ data: toSeriesRows(series, categories), x: 'category', y: 'value', series: 'series', layout: 'grouped' }),
    ];
  }
  // A horizontal bar runs its categories down the y axis and its values along x.
  if (type === 'bar') {
    return [
      bar({
        data: toSeriesRows(series, categories),
        x: 'value',
        y: 'category',
        series: 'series',
        layout: 'grouped',
        orientation: 'horizontal',
      }),
    ];
  }

  return series.flatMap((s) => {
    const rows: CartesianRow[] = s.data.map((value, index) => ({ category: categories?.[index] ?? index, value }));
    const options = { data: rows, x: 'category', y: 'value', seriesName: s.name } as const;
    switch (type) {
      case 'area':
        // Overlaid line + wash per series, the look of the chart this replaced —
        // a bare area is opaque and a multi-series area mark would stack.
        return [area({ ...options, fillOpacity: AREA_FILL_OPACITY }), line(options)];
      case 'spline':
        return [line({ ...options, curve: 'smooth' })];
      case 'line':
        return [line(options)];
      default: {
        const _exhaustive: never = type;
        return [line(options)];
      }
    }
  });
}

function CartesianChartRenderer({ series, type, categories }: ChartProps) {
  // Stable marks reference prevents the chart re-animating on every streaming
  // token when the props haven't actually changed.
  const marks = useMemo(() => {
    const resolvedType = type ?? 'line';
    const resolvedSeries = completeSeries(series);
    if (resolvedType === 'pie') {
      return pieMarks(resolvedSeries, categories);
    }
    // Sankey never reaches this renderer (ChartRenderer routes it), but the
    // prop type carries it.
    if (resolvedType === 'sankey') {
      return [];
    }
    return cartesianMarks(resolvedType, resolvedSeries, categories);
  }, [series, type, categories]);
  return <Chart ariaLabel="Chart" height={300} marks={marks} withLegend />;
}

// Exported for its tests; the library definition below is the only render-time consumer.
export function ChartRenderer({ series, type, categories, links }: ChartProps) {
  if (type === 'sankey') {
    if (links == null) return null;
    return <SankeyRenderer links={links} />;
  }
  return <CartesianChartRenderer series={series} type={type} categories={categories} />;
}

const ChartDef = defineComponent({
  name: 'Chart',
  description: 'Renders a data visualization. Use type="sankey" with links for flow diagrams.',
  props: z.object({
    series: z.array(SeriesSchema).optional(),
    type: z.enum(CHART_TYPES).optional(),
    categories: z.array(z.string()).optional(),
    links: z.array(SankeyLinkSchema).optional(),
  }),
  // Props are re-materialized on every streamed chunk, so a render error on a
  // half-streamed payload must not stick: reset once the payload changes. Keyed
  // on content, not array identity, so unchanged re-materializations don't
  // re-throw (and re-report) the same error.
  component: ({ props }) => (
    <AppErrorBoundary resetKeys={[JSON.stringify([props.type, props.series, props.categories, props.links])]}>
      <ChartRenderer series={props.series} type={props.type} categories={props.categories} links={props.links} />
    </AppErrorBoundary>
  ),
});

// ─── StatCard ─────────────────────────────────────────────────────────────────

const StatCardDef = defineComponent({
  name: 'StatCard',
  description: 'Displays a single metric label and value.',
  props: z.object({
    title: z.string(),
    value: z.string(),
  }),
  component: ({ props }) => (
    <AppErrorBoundary>
      <Card>
        <CardHeader>
          <CardTitle>{props.title}</CardTitle>
        </CardHeader>
        <CardSection>
          <Text size="xl" weight="semibold">
            {props.value}
          </Text>
        </CardSection>
      </Card>
    </AppErrorBoundary>
  ),
});

// ─── Card ─────────────────────────────────────────────────────────────────────

const CardDef = defineComponent({
  name: 'Card',
  description: 'A card container with an optional title. Use to wrap a Chart.',
  props: z.object({
    children: z.array(z.union([ChartDef.ref, StatCardDef.ref])),
    title: z.string().optional(),
  }),
  component: ({ props, renderNode }) => (
    <AppErrorBoundary>
      <Card>
        {props.title != null ? (
          <CardHeader>
            <CardTitle>{props.title}</CardTitle>
          </CardHeader>
        ) : null}
        <CardSection>{renderNode(props.children)}</CardSection>
      </Card>
    </AppErrorBoundary>
  ),
});

// ─── Grid ─────────────────────────────────────────────────────────────────────

const GridDef = defineComponent({
  name: 'Grid',
  description: 'Arranges cards in a multi-column layout.',
  props: z.object({
    children: z.array(z.union([StatCardDef.ref, CardDef.ref])),
    columns: z.number().optional(),
    gap: z.enum(COPILOT_GRID_GAPS).optional(),
  }),
  component: ({ props, renderNode }) => {
    const gap: GridGap = props.gap ?? '200';

    return (
      <AppErrorBoundary>
        <Grid columns={props.columns ?? 2} gap={gap}>
          {renderNode(props.children)}
        </Grid>
      </AppErrorBoundary>
    );
  },
});

// ─── Root ─────────────────────────────────────────────────────────────────────

const RootDef = defineComponent({
  name: 'Root',
  description: 'Top-level container. Every response must assign root = Root(...).',
  props: z.object({
    children: z.array(z.union([StatCardDef.ref, CardDef.ref, GridDef.ref, LinkButtonDef.ref])),
  }),
  component: ({ props, renderNode }) => (
    <AppErrorBoundary>
      <Stack gap="200">{renderNode(props.children)}</Stack>
    </AppErrorBoundary>
  ),
});

// ─── Library ──────────────────────────────────────────────────────────────────

export const copilotLibrary = createLibrary({
  root: 'Root',
  components: [LinkButtonDef, ChartDef, StatCardDef, CardDef, GridDef, RootDef],
  componentGroups: [
    {
      name: 'Navigation',
      components: ['LinkButton'],
      notes: [
        '- Use LinkButton at the end of a response to give the user a clear call-to-action to navigate somewhere relevant.',
        '- Examples: "View Transactional", "Go to Campaign", "Open Settings".',
        '- For in-platform pages, use a workspace-rooted path, taking the workspace id from the page URL (e.g. href="/workspaces/12345/journeys/transactional/678"). A path with no /workspaces/<id> does not resolve.',
        '- Prefer a single LinkButton per response. Use multiple only when there are distinct next steps.',
      ],
    },
    {
      name: 'Layout',
      components: ['Root', 'Grid'],
      notes: [
        '- Always write root = Root([...]) as the very first statement.',
        '- Grid defaults to 2 columns. Use columns=3 for stat overviews.',
      ],
    },
    {
      name: 'Cards',
      components: ['StatCard', 'Card'],
      notes: [
        '- StatCard(title, value): for a single metric; value is a pre-formatted string e.g. "1,234" or "$42K".',
        '- Card([chart], title?): use to wrap a Chart with an optional title.',
        '- Do NOT use StatCard or Card to display lists of items (e.g. campaigns, segments, broadcasts). Use plain markdown (bullet lists, numbered lists, or tables) for listing items.',
        '- Only use StatCard for numeric metrics/KPIs. Only use Card to wrap a Chart.',
      ],
    },
    {
      name: 'Charts',
      components: ['Chart'],
      notes: [
        '- Use "line" or "area" for trends over time, "column" or "bar" for comparisons, "pie" for proportions, "sankey" for flow diagrams.',
        '- For non-sankey types: series is required, data is always number[], categories provides the labels (x-axis for cartesian, slice names for pie).',
        '- categories length must match data length in every series.',
        '- Pie: pass exactly one series; categories become the slice labels.',
        '- Sankey: use type="sankey" with links instead of series. Each link has from (source node name), to (target node name), weight (numeric flow value). Node names are inferred from links. Omit series and categories.',
        '- Chart has no title — wrap it in Card to add a title.',
      ],
    },
  ],
});

export const copilotPromptOptions: Omit<PromptOptions, 'preamble'> = {
  examples: [
    // Stat grid
    `root = Root([grid])
grid = Grid([c1, c2, c3], 3)
c1 = StatCard("Total Users", "12,847")
c2 = StatCard("Active", "9,203")
c3 = StatCard("Churn Rate", "2.4%")`,

    // Single chart in a card
    `root = Root([card])
card = Card([chart], "Monthly Signups")
chart = Chart([{name:"Signups",data:[120,145,132,178]}], "line", ["Jan","Feb","Mar","Apr"])`,

    // Chart + stat grid
    `root = Root([grid, card])
grid = Grid([c1, c2], 2)
c1 = StatCard("Total Revenue", "$148K")
c2 = StatCard("Growth", "+18%")
card = Card([chart], "Revenue Trend")
chart = Chart([{name:"Revenue",data:[42,51,48,63,55,70]}], "area", ["Jan","Feb","Mar","Apr","May","Jun"])`,

    // Pie chart — one series, categories become slice labels
    `root = Root([card])
card = Card([chart], "Messages by Channel")
chart = Chart([{name:"Channels",data:[38,22,18,14,8]}], "pie", ["Email","Push","SMS","In-App","Webhook"])`,

    // Sankey — user flow breakdown
    `root = Root([card])
card = Card([chart], "Message Delivery Flow")
chart = Chart([], "sankey", [], [{from:"Sent",to:"Delivered",weight:8500},{from:"Sent",to:"Bounced",weight:1500},{from:"Delivered",to:"Opened",weight:5200},{from:"Delivered",to:"Ignored",weight:3300},{from:"Opened",to:"Clicked",weight:2100},{from:"Opened",to:"No Click",weight:3100}])`,

    // LinkButton — navigate to a relevant page
    `root = Root([btn])
btn = LinkButton("View Transactional", "/transactional/123")`,

    // Stats with a link to dig deeper
    `root = Root([grid, btn])
grid = Grid([c1, c2], 2)
c1 = StatCard("Delivered", "12,847")
c2 = StatCard("Opened", "4,203")
btn = LinkButton("View Campaign", "/campaigns/456")`,
  ],
};
